import { Box, SxProps, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import {
  BundleEntry,
  Organization,
  Patient,
  Questionnaire,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
} from 'fhir/r4b';
import { enqueueSnackbar } from 'notistack';
import { FC, ReactElement, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { AboutPatientContainer } from 'src/features/visits/shared/components/patient/AboutPatientContainer';
import { ActionBar } from 'src/features/visits/shared/components/patient/ActionBar';
import { AddInsuranceModal } from 'src/features/visits/shared/components/patient/AddInsuranceModal';
import { BreadCrumbs } from 'src/features/visits/shared/components/patient/BreadCrumbs';
import { ContactContainer } from 'src/features/visits/shared/components/patient/ContactContainer';
import { Header } from 'src/features/visits/shared/components/patient/Header';
import { InsuranceSection } from 'src/features/visits/shared/components/patient/InsuranceSection';
import { PatientDetailsContainer } from 'src/features/visits/shared/components/patient/PatientDetailsContainer';
// import { PrimaryCareContainer } from 'src/features/visits/shared/components/patient/PrimaryCareContainer';
// import { ResponsibleInformationContainer } from 'src/features/visits/shared/components/patient/ResponsibleInformationContainer';
import { WarningBanner } from 'src/features/visits/shared/components/patient/WarningBanner';
import { useOystehrAPIClient } from 'src/features/visits/shared/hooks/useOystehrAPIClient';
import {
  CoverageWithPriority,
  extractFirstValueFromAnswer,
  flattenItems,
  InsurancePlanDTO,
  makePrepopulatedItemsFromPatientRecord,
  OrderedCoveragesWithSubscribers,
  PatientAccountResponse,
  pruneEmptySections,
} from 'utils';
import ehrInsuranceUpdateFormJson from '../../../../config/oystehr/ehr-insurance-update-questionnaire.json';
import { CustomDialog } from '../components/dialogs';
import { LoadingScreen } from '../components/LoadingScreen';
import { INSURANCE_COVERAGE_OPTIONS, InsurancePriorityOptions } from '../constants';
import { structureQuestionnaireResponse } from '../helpers/qr-structure';
import {
  useGetInsurancePlans,
  useGetPatient,
  useGetPatientAccount,
  useGetPatientCoverages,
  useRemovePatientCoverage,
  useUpdatePatientAccount,
} from '../hooks/useGetPatient';
import { createInsurancePlanDto, usePatientStore } from '../state/patient.store';

const COVERAGE_ITEMS = ['insurance-section', 'insurance-section-2'];
const ANSWER_TYPES: ('String' | 'Boolean' | 'Reference' | 'Attachment')[] = [
  'String',
  'Boolean',
  'Reference',
  'Attachment',
];

const getAnyAnswer = (item: QuestionnaireResponseItem): any | undefined => {
  for (let i = 0; i < ANSWER_TYPES.length; i++) {
    const answer = extractFirstValueFromAnswer(item.answer ?? [], ANSWER_TYPES[i]);
    if (answer !== undefined) {
      return answer;
    }
  }
  return undefined;
};

const makeFormDefaults = (currentItemValues: QuestionnaireResponseItem[]): Record<string, any> => {
  const flattened = flattenItems(currentItemValues);
  return flattened.reduce((acc: Record<string, any>, item: QuestionnaireResponseItem) => {
    const value = getAnyAnswer(item);
    acc[item.linkId] = value;
    return acc;
  }, {});
};

const makePrepopulatedCoveragesFormDefaults = ({
  coverages,
  patient,
  insuranceOrgs,
  questionnaire,
}: {
  coverages: OrderedCoveragesWithSubscribers;
  patient: Patient;
  insuranceOrgs: Organization[];
  questionnaire: Questionnaire;
}): Record<string, any> => {
  if (!questionnaire?.item) return {};

  const filteredQuestionnaire: Questionnaire = {
    ...questionnaire,
    item: questionnaire.item.filter((item) => COVERAGE_ITEMS.includes(item.linkId)),
  };

  const prepopulatedItems = makePrepopulatedItemsFromPatientRecord({
    coverages,
    patient,
    insuranceOrgs,
    questionnaire: filteredQuestionnaire,
    coverageChecks: [],
  });

  return makeFormDefaults(prepopulatedItems);
};

const clearPCPFieldsIfInactive = (values: Record<string, any>): Record<string, any> => {
  return Object.fromEntries(
    Object.entries(values).map(([key, value]) => [
      key,
      !values['pcp-active'] && key.startsWith('pcp-') && key !== 'pcp-active' ? '' : value,
    ])
  );
};

const transformInsurancePlans = (bundleEntries: BundleEntry[]): InsurancePlanDTO[] => {
  const organizations = bundleEntries
    .filter((bundleEntry) => bundleEntry.resource?.resourceType === 'Organization')
    .map((bundleEntry) => bundleEntry.resource as Organization);

  const transformedPlans = organizations
    .map((organization) => {
      try {
        return createInsurancePlanDto(organization);
      } catch (err) {
        console.error(err);
        console.error('Could not add insurance org due to incomplete data:', JSON.stringify(organization));
        return {} as InsurancePlanDTO;
      }
    })
    .filter((insurancePlan) => insurancePlan.id !== undefined)
    .sort((a, b) => a.name.localeCompare(b.name));

  const insurancePlanMap: Record<string, InsurancePlanDTO> = {};
  transformedPlans.forEach((plan) => {
    insurancePlanMap[plan.name ?? ''] = plan;
  });

  return Object.values(insurancePlanMap);
};

const usePatientData = (
  id: string | undefined,
  questionnaireResponse?: QuestionnaireResponse
): {
  accountData?: PatientAccountResponse;
  insuranceData?: {
    coverages: OrderedCoveragesWithSubscribers;
    insuranceOrgs: Organization[];
  };
  questionnaire?: Questionnaire;
  coverages: CoverageWithPriority[];
  patient?: Patient;
  isFetching: boolean;
  defaultFormVals: any;
  coveragesFetching: boolean;
} => {
  const apiClient = useOystehrAPIClient();

  const { isFetching: accountFetching, data: accountData } = useGetPatientAccount({
    apiClient,
    patientId: id ?? null,
  });

  const { data: insuranceData, isFetching: coveragesFetching } = useGetPatientCoverages({
    apiClient,
    patientId: id ?? null,
  });

  const maybeQuestionnaire = Object.values(ehrInsuranceUpdateFormJson.fhirResources).find(
    (q) =>
      q.resource.resourceType === 'Questionnaire' &&
      q.resource.status === 'active' &&
      q.resource.url.includes('ehr-insurance-update-questionnaire')
  )?.resource as Questionnaire | undefined;
  if (!maybeQuestionnaire) {
    throw new Error('Could not find insurance update questionnaire in config');
  }
  const questionnaire = maybeQuestionnaire;

  const coverages: CoverageWithPriority[] = useMemo(() => {
    if (!insuranceData?.coverages) return [];

    const result: CoverageWithPriority[] = [];
    if (insuranceData.coverages.primary) {
      result.push({ resource: insuranceData.coverages.primary, startingPriority: 1 });
    }
    if (insuranceData.coverages.secondary) {
      result.push({ resource: insuranceData.coverages.secondary, startingPriority: 2 });
    }

    return result;
  }, [insuranceData?.coverages]);

  const { patient, isFetching, defaultFormVals } = useMemo(() => {
    const patient = accountData?.patient;
    const isFetching = accountFetching;

    let defaultFormVals: any;
    if (!isFetching && accountData && questionnaire) {
      const prepopulatedForm = makePrepopulatedItemsFromPatientRecord({
        ...accountData,
        coverages: {},
        insuranceOrgs: [],
        questionnaire,
      });
      defaultFormVals = makeFormDefaults(prepopulatedForm);

      if (questionnaireResponse) {
        const qrItems = flattenItems(questionnaireResponse.item || []);
        const raceMapping: Record<string, string> = {
          asian: 'Asian',
          black: 'Black or African American',
          white: 'White',
          native: 'American Indian or Alaska Native',
          pacific: 'Native Hawaiian or Other Pacific Islander',
          other: 'Other',
          'prefer-not-to-say': 'Decline to Specify',
        };
        const ethnicityMapping: Record<string, string> = {
          hispanic: 'Hispanic or Latino',
          'non-hispanic': 'Not Hispanic or Latino',
          'prefer-not-to-say': 'Decline to Specify',
        };
        const pronounsMapping: Record<string, string> = {
          'he/him': 'He/him',
          'she/her': 'She/her',
          'they/them': 'They/them',
        };

        Object.keys(defaultFormVals).forEach((key) => {
          if (!defaultFormVals[key]) {
            const qrItem = qrItems.find((item: QuestionnaireResponseItem) => item.linkId === key);
            if (qrItem) {
              let answer = getAnyAnswer(qrItem);
              if (key === 'patient-race' && answer && raceMapping[answer as string]) {
                answer = raceMapping[answer as string];
              } else if (key === 'patient-ethnicity' && answer && ethnicityMapping[answer as string]) {
                answer = ethnicityMapping[answer as string];
              } else if (key === 'patient-pronouns' && answer && pronounsMapping[answer as string]) {
                answer = pronounsMapping[answer as string];
              }
              defaultFormVals[key] = answer;
            }
          }
        });
      }
    }

    return { patient, isFetching, defaultFormVals };
  }, [accountData, questionnaire, accountFetching, questionnaireResponse]);

  return {
    accountData,
    insuranceData,
    questionnaire,
    coverages,
    patient,
    isFetching,
    defaultFormVals,
    coveragesFetching,
  };
};

const useFormData = (
  defaultFormVals: any,
  coveragesFetching: boolean,
  insuranceData: any,
  questionnaire: Questionnaire | undefined,
  accountData: any
): {
  methods: ReturnType<typeof useForm>;
  coveragesFormValues: any;
} => {
  const methods = useForm({
    defaultValues: defaultFormVals,
    values: defaultFormVals,
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const { coveragesFormValues } = useMemo(() => {
    let coveragesFormValues: any;
    if (
      !coveragesFetching &&
      insuranceData?.coverages &&
      insuranceData?.insuranceOrgs &&
      questionnaire &&
      accountData
    ) {
      const formDefaults = makePrepopulatedCoveragesFormDefaults({
        coverages: insuranceData.coverages,
        patient: accountData.patient,
        insuranceOrgs: insuranceData.insuranceOrgs,
        questionnaire,
      });
      coveragesFormValues = { ...formDefaults };
    }
    return { coveragesFormValues };
  }, [accountData, coveragesFetching, questionnaire, insuranceData?.coverages, insuranceData?.insuranceOrgs]);

  useEffect(() => {
    if (!coveragesFormValues || Object.keys(coveragesFormValues).length === 0) return;

    Object.entries(coveragesFormValues).forEach(([key, value]) => {
      methods.setValue(key, value, {
        shouldDirty: false,
        shouldTouch: false,
      });
    });
  }, [coveragesFormValues, methods]);

  return { methods, coveragesFormValues };
};

const useMutations = (): {
  submitQR: ReturnType<typeof useUpdatePatientAccount>;
  removeCoverage: ReturnType<typeof useRemovePatientCoverage>;
  queryClient: ReturnType<typeof useQueryClient>;
} => {
  const queryClient = useQueryClient();

  const submitQR = useUpdatePatientAccount(async () => {
    await queryClient.invalidateQueries({ queryKey: ['patient-account-get'] });
    await queryClient.invalidateQueries({ queryKey: ['patient-coverages'] });
  });

  const removeCoverage = useRemovePatientCoverage();

  return { submitQR, removeCoverage, queryClient };
};

interface PatientAccountComponentProps {
  id: string | undefined;
  title?: string;
  renderBreadCrumbs?: boolean;
  renderHeader?: boolean;
  containerSX?: SxProps;
  loadingComponent?: ReactElement;
  renderBackButton?: boolean;
  questionnaireResponse?: QuestionnaireResponse;
}

export const PatientAccountComponent: FC<PatientAccountComponentProps> = ({
  id,
  title,
  renderBreadCrumbs = false,
  renderHeader = false,
  containerSX = {},
  loadingComponent = <LoadingScreen />,
  renderBackButton = true,
  questionnaireResponse,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setInsurancePlans } = usePatientStore();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    accountData,
    insuranceData,
    questionnaire,
    coverages,
    patient,
    isFetching,
    defaultFormVals,
    coveragesFetching,
  } = usePatientData(id, questionnaireResponse);

  const { methods, coveragesFormValues } = useFormData(
    defaultFormVals,
    coveragesFetching,
    insuranceData,
    questionnaire,
    accountData
  );

  const { submitQR, removeCoverage, queryClient } = useMutations();

  const { otherPatientsWithSameName, setOtherPatientsWithSameName } = useGetPatient(id);

  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [openAddInsuranceModal, setOpenAddInsuranceModal] = useState(false);

  useGetInsurancePlans((data) => {
    if (!data) return;

    const bundleEntries = data.entry;
    if (bundleEntries) {
      const uniquePlans = transformInsurancePlans(bundleEntries);
      setInsurancePlans(uniquePlans);
    }
  });

  const { handleSubmit, watch, formState } = methods;
  const { dirtyFields } = formState;

  useEffect(() => {
    if (defaultFormVals && formState.isSubmitSuccessful && submitQR.isSuccess) {
      methods.reset();
    }
  }, [defaultFormVals, methods, formState.isSubmitSuccessful, submitQR.isSuccess]);

  useEffect(() => {
    if (!coveragesFormValues || Object.keys(coveragesFormValues).length === 0) return;

    Object.entries(coveragesFormValues).forEach(([key, value]) => {
      methods.setValue(key, value, {
        shouldDirty: false,
        shouldTouch: false,
      });
    });
  }, [coveragesFormValues, methods]);

  const handleDiscardChanges = (): void => {
    methods.reset();
    setOpenConfirmationDialog(false);
    navigate(-1);
  };

  const handleCloseConfirmationDialog = (): void => {
    setOpenConfirmationDialog(false);
  };

  const handleBackClickWithConfirmation = (): void => {
    if (Object.keys(dirtyFields).length > 0) {
      setOpenConfirmationDialog(true);
    } else {
      navigate(-1);
    }
  };

  const handleSaveForm = async (values: any): Promise<void> => {
    if (!questionnaire || !patient?.id) {
      enqueueSnackbar(t('patientProfile.errors.somethingWentWrong'), { variant: 'error' });
      return;
    }

    const filteredValues = clearPCPFieldsIfInactive(values);
    const qr = pruneEmptySections(structureQuestionnaireResponse(questionnaire, filteredValues, patient.id));
    submitQR.mutate(qr);
  };

  const handleRemoveCoverage = (coverageId: string): void => {
    if (patient?.id) {
      removeCoverage.mutate(
        {
          patientId: patient.id,
          coverageId,
        },
        {
          onSuccess: () => {
            enqueueSnackbar(t('patientProfile.insurance.removed'), { variant: 'success' });
            void queryClient.invalidateQueries({ queryKey: ['patient-coverages'] });
          },
          onError: () => {
            enqueueSnackbar(t('patientProfile.errors.saveFailed'), {
              variant: 'error',
            });
          },
        }
      );
    }
  };

  if ((isFetching || coveragesFetching) && !patient) {
    return loadingComponent;
  }

  if (!patient) return null;

  const currentlyAssignedPriorities = watch(InsurancePriorityOptions);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: { xs: '100vh', sm: 'auto' },
        overflow: { xs: 'hidden', sm: 'visible' },
      }}
    >
      {isFetching && <LoadingScreen />}
      <FormProvider {...methods}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: { xs: 1, sm: 'none' },
            overflow: { xs: 'hidden', sm: 'visible' },
          }}
        >
          {renderHeader && <Header handleDiscard={handleBackClickWithConfirmation} id={id} />}
          <Box
            sx={{
              flex: { xs: 1, sm: 'none' },
              overflowY: { xs: 'auto', sm: 'visible' },
              overflowX: 'hidden',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', ...containerSX, marginBottom: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {renderBreadCrumbs && <BreadCrumbs patient={patient} />}
                {title && (
                  <Typography variant="h3" color="primary.main">
                    {title}
                  </Typography>
                )}
                <WarningBanner
                  otherPatientsWithSameName={otherPatientsWithSameName}
                  onClose={() => setOtherPatientsWithSameName(false)}
                />
                <Box sx={{ display: 'flex', gap: 3, flexDirection: isMobile ? 'column' : 'row' }}>
                  <Box sx={{ flex: '1 1', display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <AboutPatientContainer isLoading={isFetching || submitQR.isPending} />
                    <ContactContainer isLoading={isFetching || submitQR.isPending} />
                    {/* <PrimaryCareContainer isLoading={isFetching || submitQR.isPending} /> */}
                  </Box>
                  <Box sx={{ flex: '1 1', display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <PatientDetailsContainer patient={patient} isLoading={isFetching || submitQR.isPending} />
                    <InsuranceSection
                      coverages={coverages}
                      patient={patient}
                      accountData={accountData}
                      removeCoverage={removeCoverage}
                      onRemoveCoverage={handleRemoveCoverage}
                      onAddInsurance={() => setOpenAddInsuranceModal(true)}
                    />
                    {/* <ResponsibleInformationContainer isLoading={isFetching || submitQR.isPending} /> */}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
          <ActionBar
            handleDiscard={handleBackClickWithConfirmation}
            handleSave={handleSubmit(handleSaveForm, () => {
              enqueueSnackbar(t('patientProfile.errors.fixValidations'), { variant: 'error' });
            })}
            loading={submitQR.isPending}
            hidden={false}
            submitDisabled={Object.keys(dirtyFields).length === 0}
            backButtonHidden={renderBackButton === false}
          />
        </Box>
        <CustomDialog
          open={openConfirmationDialog}
          handleClose={handleCloseConfirmationDialog}
          title={t('patientProfile.modals.discardChanges.title')}
          description={t('patientProfile.modals.discardChanges.description')}
          closeButtonText={t('patientProfile.buttons.cancel')}
          handleConfirm={handleDiscardChanges}
          confirmText={t('patientProfile.buttons.discardChanges')}
        />
      </FormProvider>
      <AddInsuranceModal
        open={openAddInsuranceModal}
        onClose={() => setOpenAddInsuranceModal(false)}
        questionnaire={questionnaire ?? { resourceType: 'Questionnaire', status: 'draft' }}
        patientId={patient.id ?? ''}
        priorityOptions={INSURANCE_COVERAGE_OPTIONS.filter(
          (option) => !currentlyAssignedPriorities.includes(option.value)
        )}
      />
    </Box>
  );
};

const PatientInformationPage: FC = () => {
  const { id } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  return (
    <PatientAccountComponent
      id={id}
      title={t('patientProfile.title')}
      renderBreadCrumbs
      renderHeader
      containerSX={{ padding: isMobile ? theme.spacing(2) : theme.spacing(3) }}
    />
  );
};

export default PatientInformationPage;
